function id() {
  return crypto.randomUUID();
}

export function createTemplateDocument(nodes = []) {
  return { nodes };
}

export function createTextNode(text = '') {
  return { id: id(), type: 'text', text };
}

export function createEntityStateNode() {
  return { id: id(), type: 'entity_state', entityId: '', label: '', fallback: '' };
}

export function createEntityAttributeNode() {
  return { id: id(), type: 'entity_attribute', entityId: '', attribute: '', label: '', fallback: '' };
}

export function createDateTimeNode(value = 'time') {
  return { id: id(), type: 'datetime', value, label: DATE_TIME_VALUES.find(v => v.value === value)?.label ?? value };
}

export function createConditionNode() {
  return {
    id: id(),
    type: 'condition',
    condition: {
      source: { type: 'entity_state', entityId: '' },
      operator: 'equals',
      value: '',
    },
    whenTrue: createTemplateDocument([createTextNode('')]),
    whenFalse: createTemplateDocument([]),
  };
}

export const DATE_TIME_VALUES = [
  { value: 'time', label: 'Current time' },
  { value: 'hour', label: 'Hour' },
  { value: 'minute', label: 'Minute' },
  { value: 'second', label: 'Second' },
  { value: 'date', label: 'Date' },
  { value: 'weekday', label: 'Weekday' },
  { value: 'day', label: 'Day' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

export function documentHasSpokenContent(document) {
  return (document?.nodes ?? []).some(node => {
    if (node.type === 'text') return node.text.trim().length > 0;
    if (node.type === 'condition') {
      return documentHasSpokenContent(node.whenTrue) || documentHasSpokenContent(node.whenFalse);
    }
    return ['entity_state', 'entity_attribute', 'datetime'].includes(node.type);
  });
}

export function validateTemplateDocument(document) {
  return (document?.nodes ?? []).every(node => {
    if (node.type === 'text') return !['{{', '{%', '{#'].some(marker => node.text.includes(marker));
    if (node.type === 'datetime') return true;
    if (node.type === 'entity_state') return Boolean(node.entityId);
    if (node.type === 'entity_attribute') return Boolean(node.entityId && node.attribute);
    if (node.type !== 'condition') return false;
    const condition = node.condition;
    const sourceValid = condition?.source?.entityId &&
      (condition.source.type !== 'entity_attribute' || condition.source.attribute);
    const valueRequired = !['is_on', 'is_off'].includes(condition?.operator);
    const valueValid = !valueRequired || ![undefined, null, ''].includes(condition.value);
    const numeric = ['greater_than', 'greater_or_equal', 'less_than', 'less_or_equal'].includes(condition?.operator);
    return Boolean(sourceValid && condition.operator && valueValid &&
      (!numeric || Number.isFinite(Number(condition.value))) &&
      validateTemplateDocument(node.whenTrue) &&
      validateTemplateDocument(node.whenFalse));
  });
}

export function createTtsTitle(document) {
  const nodes = document?.nodes ?? [];
  if (nodes.length === 1 && nodes[0].type === 'text') {
    return nodes[0].text.replace(/\s+/g, ' ').trim().slice(0, 80) || 'Text to speech';
  }
  return 'Text to speech';
}

export function parseSimpleTemplateDocument(source) {
  const patterns = [
    {
      regex: /\{\{\s*states\('([a-z0-9_.]+)'\)\s*\}\}/g,
      node: match => ({ ...createEntityStateNode(), entityId: match[1], label: match[1] }),
    },
    {
      regex: /\{\{\s*state_attr\('([a-z0-9_.]+)',\s*'([a-z0-9_]+)'\)\s*\}\}/g,
      node: match => ({
        ...createEntityAttributeNode(),
        entityId: match[1],
        attribute: match[2],
        label: `${match[1]} · ${match[2]}`,
      }),
    },
  ];
  const datePatterns = new Map([
    ["{{ now().strftime('%H:%M') }}", 'time'],
    ['{{ now().hour }}', 'hour'],
    ['{{ now().minute }}', 'minute'],
    ['{{ now().second }}', 'second'],
    ["{{ now().strftime('%d-%m-%Y') }}", 'date'],
    ["{{ now().strftime('%A') }}", 'weekday'],
    ['{{ now().day }}', 'day'],
    ["{{ now().strftime('%B') }}", 'month'],
    ['{{ now().year }}', 'year'],
  ]);

  const matches = [];
  for (const pattern of patterns) {
    for (const match of source.matchAll(pattern.regex)) {
      matches.push({ index: match.index, length: match[0].length, node: pattern.node(match) });
    }
  }
  for (const [template, value] of datePatterns) {
    let index = source.indexOf(template);
    while (index >= 0) {
      matches.push({ index, length: template.length, node: createDateTimeNode(value) });
      index = source.indexOf(template, index + template.length);
    }
  }
  matches.sort((a, b) => a.index - b.index);

  const nodes = [];
  let offset = 0;
  for (const match of matches) {
    if (match.index < offset) continue;
    const literal = source.slice(offset, match.index);
    if (literal.includes('{')) return null;
    if (literal) nodes.push(createTextNode(literal));
    nodes.push(match.node);
    offset = match.index + match.length;
  }
  const tail = source.slice(offset);
  if (tail.includes('{')) return null;
  if (tail || nodes.length === 0) nodes.push(createTextNode(tail));
  return createTemplateDocument(nodes);
}
